import { shallowEqual, useSelector } from 'react-redux';
import type { CustomerVehiclesState, GroupedDeal, StateTree } from '../types';
import type { ITradeIn } from '@makemydeal/dr-platform-types';
import { customerVehicleSelector } from '../store/config';
import { getDeduplicatedTradeIns } from '../utils/utils';

export const useCustomerVehiclesSelector = (): CustomerVehiclesState => {
    const state: CustomerVehiclesState = useSelector((state: StateTree) => {
        const localState = state.customerVehicles;

        // Get customerFirstName from consuming app via additionalSelectors
        const customerFirstName = customerVehicleSelector?.additionalSelectors?.getCustomerFirstName?.(state);

        // dealsCount is maintained by the reducer (derived from `deals` on FETCH_DEALS_SUCCESS).
        const dealsCount = localState?.dealsCount ?? 0;

        // Get current deal identifiers from consuming app via additionalSelectors
        const currentDealId = customerVehicleSelector?.additionalSelectors?.getCurrentDealId?.(state);
        const currentDealVersion = customerVehicleSelector?.additionalSelectors?.getCurrentDealVersion?.(state);

        return {
            ...localState,
            customerFirstName,
            dealsCount,
            currentDealId,
            currentDealVersion
        };
    }, shallowEqual); // merges localState with cross-slice fields, producing a new object each call; shallowEqual prevents re-renders when no field has changed
    return state;
};

/**
 * Returns deduplicated trade-ins across ALL deals in customerVehicles state.
 * Each deal can have its own tradeIns array; this collects them all and deduplicates
 * by VIN (keeping the first occurrence, which is the most recently updated deal).
 * For VIN-less trades, all are included since they can't be deduplicated by VIN.
 * Structure: state.customerVehicles.deals[].tradeIns[]
 */
export const useCurrentDealTradeIns = (): ITradeIn[] => {
    return useSelector((state: StateTree) => {
        const deals = state.customerVehicles?.deals ?? [];
        return getDeduplicatedTradeIns(deals);
    });
};

/**
 * Selector to get trade-in modal visibility state.
 * Can be used in middleware or other contexts where hooks aren't available.
 */
export const getIsTradeInModalVisible = (state: StateTree): boolean => {
    return state.customerVehicles?.isTradeInModalVisible ?? false;
};

/**
 * Selector to get the grouped deals list.
 * Can be used in middleware or other contexts where hooks aren't available.
 */
export const getCustomerVehicleDeals = (state: StateTree): GroupedDeal[] => {
    return state.customerVehicles?.deals ?? [];
};
